<script lang="ts">
  import EventPill from './EventPill.svelte';
  import RowHeader from './RowHeader.svelte';
  import { ui, config, focus, selection, toggleSelected, openHoverPreview, closeHoverPreviewSoon, isKiosk, rescheduleLocalEvents, focusEventByUid, mergedVisibleFor, rowInk, lightRowInk } from '../lib/state.svelte';
  import { dateToPx, pxToDate, LANE_HEIGHT, ROW_PADDING_PX, MIN_VISUAL_PILL_PX, type LocationBand } from '../lib/layout';
  import { formatDate, zonedDateProxy } from '../lib/format';
  import { MS_PER_DAY } from '../lib/time';
  import { HOLD_SLOP_PX } from '../lib/marker-hold';
  import { tap } from '../lib/haptics';
  import {
    applyDragChange,
    createDaySnap,
    dragMembers,
    formatDragReadout,
    isNoopChange,
    type DragChange,
  } from '../lib/event-drag';
  import type { DragSource } from '../lib/event-drag-gesture';
  import { today, todayStartMs } from '../lib/today.svelte';
  import { clock } from '../lib/clock.svelte';
  import type { CalendarFeed, DisplayEvent, LaneEvent, StyleVariant } from '../lib/types';

  type Props = {
    feed: CalendarFeed;
    events: DisplayEvent[];
    laneEvents: LaneEvent[];
    // Location bands (feed.subLanes === 'location'); empty for a packed row.
    bands?: LocationBand[];
    rangeStart: Date;
    totalWidth: number;
    pxPerDay: number;
    bodyHeight: number;
    // Lanes the row is tall enough for; pills on lower lanes (past events while
    // the view isn't in the past) aren't drawn.
    laneLimit?: number;
    matchUids: Set<string>;
    currentMatchUid: string | null;
    scrollEl: HTMLElement | undefined;
    dayTicksPx: { px: number; past: boolean }[];
    monthStartsPx: { px: number; past: boolean }[];
    thickStrips: { left: number; width: number }[];
    thinStrips: { left: number; width: number }[];
    weekendStrips: { left: number; width: number; past: boolean }[];
    holidayStrips: { left: number; width: number }[];
    // Days a thin (tentative) global block covers: Timeline draws them as a
    // discreet band over every row; the header repeats them like holidayStrips.
    thinBandStrips: { left: number; width: number }[];
    rowIndex: number;
    visibleLeft: number;
    visibleRight: number;
  };
  function isHighlightedDot(ev: DisplayEvent, idx: number): boolean {
    if (currentMatchUid && currentMatchUid === ev.uid) return true;
    if (focus.feedId === feed.id && focus.eventIndex === idx) return true;
    return false;
  }
  const {
    feed,
    events,
    laneEvents,
    bands = [],
    rangeStart,
    totalWidth,
    pxPerDay,
    bodyHeight,
    laneLimit = Infinity,
    matchUids,
    currentMatchUid,
    scrollEl,
    dayTicksPx,
    monthStartsPx,
    thickStrips,
    thinStrips,
    weekendStrips,
    holidayStrips,
    thinBandStrips,
    rowIndex,
    visibleLeft,
    visibleRight,
  }: Props = $props();

  const visibleEvents = $derived(
    events.filter((e) => !e.hidden || e.styleVariant === 'hidden'),
  );
  const sortedLaneEvents = $derived(
    // Start, then uid: the same order as the keyboard-nav list
    // (mergeConsecutiveDays), which focus.eventIndex indexes — a location
    // layout hands events back grouped by band, so ties must break the same way.
    [...laneEvents].sort(
      (a, b) => a.start.getTime() - b.start.getTime() || (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0),
    ),
  );

  // Viewport virtualization: only render nodes intersecting the scroll window
  // (with overscan, computed in Timeline). Nothing renders until the window is
  // measured: the timeline stays hidden until it's centred anyway, and building
  // every pill of a 1–2 year range just to throw most away cost startup dearly.
  // Timeline measures on mount, before the first paint.
  function inWindow(left: number, width: number): boolean {
    if (!(visibleRight > visibleLeft)) return false;
    return left <= visibleRight && left + width >= visibleLeft;
  }
  const vThick = $derived(thickStrips.filter((o) => inWindow(o.left, o.width)));
  const vThin = $derived(thinStrips.filter((o) => inWindow(o.left, o.width)));
  // Header pattern: this feed's blocking hatch plus the global holiday band, and
  // the weekend tint — window-filtered so the header renders the same continuous
  // pattern its body shows.
  const vHoliday = $derived(holidayStrips.filter((o) => inWindow(o.left, o.width)));
  const vThinBand = $derived(thinBandStrips.filter((o) => inWindow(o.left, o.width)));
  // Header thin pattern: the thin global band plus this row's own thin hatch,
  // one strip per left (a day can be in both; keys must stay unique).
  const vHdrThin = $derived.by(() => {
    const seen = new Set<number>();
    const out: { left: number; width: number }[] = [];
    for (const o of [...vThinBand, ...vThin]) {
      if (seen.has(o.left)) continue;
      seen.add(o.left);
      out.push(o);
    }
    return out;
  });
  const vHdrWeekend = $derived(weekendStrips.filter((w) => inWindow(w.left, w.width)));
  // The header shows both the global holiday band and this feed's local thick
  // hatch; a day can be in both (e.g. overlapping holidays across feeds), which
  // would collide on `left` in the keyed {#each} and throw each_key_duplicate.
  // Keep one strip per left — visually a single hatch column anyway.
  const vHdrThick = $derived.by(() => {
    const seen = new Set<number>();
    const out: { left: number; width: number }[] = [];
    for (const o of [...vHoliday, ...vThick]) {
      if (seen.has(o.left)) continue;
      seen.add(o.left);
      out.push(o);
    }
    return out;
  });
  // Day + month separators run through the header too (matching the full-height
  // bands Timeline draws over the row bodies), so each reads as one continuous
  // vertical rule down the whole timeline instead of breaking at every header.
  const vDayTicks = $derived(dayTicksPx.filter((d) => inWindow(d.px, 0)));
  const vMonthLines = $derived(monthStartsPx.filter((m) => inWindow(m.px, 0)));
  // Preserve each event's index in the full sorted list so focus/keyboard
  // navigation (which addresses events by index) stays correct when the
  // rendered set is a filtered subset.
  // The pill being dragged stays mounted even if it leaves the window — it holds
  // the pointer capture, and unmounting it would strand the drag.
  const vLaneEvents = $derived(
    sortedLaneEvents
      .map((e, i) => ({ e, i }))
      .filter(({ e }) => (e.lane < laneLimit && inWindow(e.leftPx, e.widthPx)) || e.uid === dragGhost?.uid),
  );

  // --- Drag to reschedule (local lanes only) ---------------------------------
  // The pill recognises the gesture (event-drag-gesture); this row owns the
  // geometry: it maps the pointer to a day, previews the move as a ghost in the
  // pill's own lane, and commits through the scratchpad on release. Horizontal
  // zooms move whole days — timed events keep their clock time — and an all-day
  // pill can be resized by either edge.
  const laneH = $derived(Math.round(LANE_HEIGHT * (config.fontSize / 14)));
  const rowPad = $derived(Math.round(ROW_PADDING_PX * (config.fontSize / 14)));
  type Ghost = { uid: string; left: number; width: number; top: number; label: string };
  let dragGhost = $state<Ghost | null>(null);

  // The UTC-midnight day under a viewport x — the same mapping as Timeline's
  // marker gestures, so a pill and the marker agree on which day is which.
  function dayAtClientX(clientX: number): number | null {
    if (!scrollEl) return null;
    const rect = scrollEl.getBoundingClientRect();
    const d = pxToDate(clientX - rect.left + scrollEl.scrollLeft, rangeStart, pxPerDay);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  function dragSourceFor(e: LaneEvent): DragSource | null {
    // Not while selecting: a drag would leave the multi-select (its undo bar
    // takes the tray handle), so the selection would be lost.
    if (isKiosk() || selection.mode) return null;
    const members = dragMembers(e);
    if (!members) return null;
    return (part, x) => {
      // Edges resize a single all-day event; a merged run only moves as a whole.
      if (part !== 'body' && (!e.allDay || members.length > 1)) return null;
      const grab = dayAtClientX(x);
      if (grab == null) return null;
      const snap = createDaySnap(HOLD_SLOP_PX);
      snap.start(grab, x);
      const tz = config.timezone;
      let change: DragChange = { kind: 'shift', days: 0, minutes: 0 };
      const render = (): void => {
        const t = applyDragChange(e, change, tz);
        const dStart = dateToPx(t.start, rangeStart, pxPerDay) - dateToPx(e.start, rangeStart, pxPerDay);
        const dEnd = dateToPx(t.end, rangeStart, pxPerDay) - dateToPx(e.end, rangeStart, pxPerDay);
        dragGhost = {
          uid: e.uid,
          left: e.leftPx + dStart,
          width: Math.max(MIN_VISUAL_PILL_PX, e.widthPx + dEnd - dStart),
          top: e.lane * laneH + rowPad,
          label: formatDragReadout(t, config),
        };
      };
      render();
      return {
        move(mx) {
          const over = dayAtClientX(mx);
          if (over == null) return;
          const days = Math.round((snap.update(over, mx) - grab) / MS_PER_DAY);
          const next: DragChange =
            part === 'body' ? { kind: 'shift', days, minutes: 0 } : { kind: 'resize-days', edge: part, days };
          const prevDays = change.kind === 'shift' || change.kind === 'resize-days' ? change.days : 0;
          change = next;
          if (days !== prevDays) {
            tap();
            render();
          }
        },
        end(commit) {
          dragGhost = null;
          if (!commit || isNoopChange(change)) return;
          // A repeat dragged out of its series comes back under a new uid.
          let uid = e.uid;
          rescheduleLocalEvents(members.map((m) => m.uid), change, tz, (from, to) => {
            if (from === uid) uid = to;
          });
          // Keep the moved pill focused wherever it re-sorted to.
          if (focus.feedId === feed.id) focusEventByUid(uid);
        },
      };
    };
  }

  function focusByUid(uid: string): void {
    const idx = sortedLaneEvents.findIndex((e) => e.uid === uid);
    if (idx >= 0) focus.eventIndex = idx;
  }

  // Collapsed rows: single-day events render as dots; multi-day events render
  // as a thin bar spanning their full duration.
  const dots = $derived.by(() => {
    if (!feed.collapsed) return [] as {
      ev: DisplayEvent;
      px: number;
      leftPx: number;
      widthPx: number;
      multiDay: boolean;
      styleAttr: StyleVariant | null;
    }[];
    // Merge consecutive-day runs so a collapsed feed shows one continuous
    // span-bar instead of a row of dots (matching the expanded lane behaviour).
    // The shared memo is already start-sorted and survives zoom, so a pinch
    // only re-runs the pixel map below.
    return mergedVisibleFor(feed.id).map((ev) => {
      const rawLeft = dateToPx(ev.start, rangeStart, pxPerDay);
      const endPx = dateToPx(ev.end, rangeStart, pxPerDay);
      const rawWidth = Math.max(pxPerDay, endPx - rawLeft);
      // Clamp a boundary-crossing span-bar to the canvas so it ends at the edge
      // instead of overflowing into the trailing pad (mirrors assignLanes).
      const leftPx = Math.max(0, rawLeft);
      const widthPx = Math.max(0, Math.min(rawLeft + rawWidth, totalWidth) - leftPx);
      const styleAttr =
        ev.styleVariant !== 'none' ? ev.styleVariant : (feed.style ?? null);
      return { ev, px: leftPx, leftPx, widthPx, multiDay: widthPx > pxPerDay * 1.5, styleAttr };
    });
  });

  const vDots = $derived(
    dots.map((d, i) => ({ d, i })).filter(({ d }) => inWindow(d.leftPx, d.widthPx)),
  );

  // Start of today, as an all-day value and as the instant it begins in the
  // display zone (timed events compare against that).
  const todayMs = $derived(today.value.getTime());
  const todayTimedMs = $derived(todayStartMs(false));

  // Day-granular "past" (end before the start of today), but never dim an event
  // the now-line sits inside of: a merged/combined bar that spans the current
  // instant is current, not past.
  function isPastEvent(e: { start: Date; end: Date; allDay: boolean }): boolean {
    if (e.start.getTime() <= clock.now && clock.now < e.end.getTime()) return false;
    return e.end.getTime() < (e.allDay ? todayMs : todayTimedMs);
  }

  function dotLabel(ev: DisplayEvent): string {
    return ev.displayTitle + ' · ' + formatDate(
      ev.allDay ? ev.start : zonedDateProxy(ev.start, config.timezone),
      config.dateFormat,
      config.locale,
    );
  }

  function openDot(ev: DisplayEvent): void {
    if (selection.mode) {
      toggleSelected(ev.uid);
      return;
    }
    ui.modalEvent = ev;
  }

  // Mouse-only hover preview for collapsed dots / span-bars, mirroring EventPill —
  // replaces the native title tooltip.
  function onDotEnter(e: PointerEvent, ev: DisplayEvent): void {
    if (e.pointerType !== 'mouse') return;
    openHoverPreview(ev, (e.currentTarget as HTMLElement).getBoundingClientRect());
  }
  function onDotLeave(e: PointerEvent): void {
    if (e.pointerType !== 'mouse') return;
    closeHoverPreviewSoon();
  }

  const isFocusedRow = $derived(focus.feedId === feed.id);

  // Location bands the row is tall enough to show. A rule marks where each band
  // after the first begins; a label names every located band (the no-location
  // band, always first among the standing ones, stays unlabelled). Standing
  // bands' labels stay at the viewport's left edge. Past bands can share lanes
  // at different times, so each of those is labelled where its own pills start,
  // and only drawn while they're in the rendered window. A row with a single
  // unlabelled band draws nothing.
  const standingBands = $derived(bands.filter((b) => !b.past && b.startLane < laneLimit));
  const pastBands = $derived(
    bands.filter((b) => b.past && b.startLane < laneLimit && inWindow(b.leftPx, b.rightPx - b.leftPx)),
  );
  // Standing rules run the whole row; a past band's rule only spans its own
  // pills, since another past band sharing its lanes may be laid out differently.
  const bandRules = $derived.by(() => {
    const out: { key: string; lane: number; left: number; width: number }[] = [];
    const seen = new Set<number>();
    for (const b of standingBands) {
      if (b.startLane > 0 && !seen.has(b.startLane)) {
        seen.add(b.startLane);
        out.push({ key: 's' + b.startLane, lane: b.startLane, left: 0, width: totalWidth });
      }
    }
    for (const b of pastBands) {
      if (b.startLane > 0) out.push({ key: 'p' + b.startLane + ':' + b.leftPx, lane: b.startLane, left: b.leftPx, width: b.rightPx - b.leftPx });
    }
    return out;
  });
  const drawBands = $derived(
    standingBands.length + pastBands.length > 1 || [...standingBands, ...pastBands].some((b) => b.label !== ''),
  );
  const bandLabelTop = (b: LocationBand): number => Math.max(rowPad, b.startLane * laneH + rowPad - 1);
</script>

<section class="row" data-feed-id={feed.id} data-category={feed.category} data-collapsed={feed.collapsed ? 'true' : null} data-ink={rowInk.feedId === feed.id ? 'true' : null} onfocusin={() => lightRowInk(feed.id)}>
  <RowHeader
    {feed}
    {visibleEvents}
    {rangeStart}
    {pxPerDay}
    {scrollEl}
    {rowIndex}
    weekendStrips={vHdrWeekend}
    thickStrips={vHdrThick}
    thinStrips={vHdrThin}
    dayLines={vDayTicks}
    monthLines={vMonthLines}
  />
  {#if !feed.collapsed}
    <div class="row-body" style="height: {bodyHeight}px;">
      {#each vThick as o (o.left)}
        <i class="holiday-strip" style="left: {o.left}px; width: {o.width}px"></i>
      {/each}
      {#each vThin as o (o.left)}
        <i class="observance-strip" style="left: {o.left}px; width: {o.width}px"></i>
      {/each}
      {#if drawBands}
        {#each bandRules as r (r.key)}
          <i class="band-rule" style="top: {r.lane * laneH + rowPad - 1}px; left: {r.left}px; width: {r.width}px;" aria-hidden="true"></i>
        {/each}
        <!-- Zero-size and sticky, so the labels stay at the viewport's left edge. -->
        <div class="band-labels" aria-hidden="true">
          {#each standingBands as b (b.startLane)}
            {#if b.label}
              <span class="band-label" style="top: {bandLabelTop(b)}px;">{b.label}</span>
            {/if}
          {/each}
        </div>
        <div class="band-labels band-labels-past" aria-hidden="true">
          {#each pastBands as b (b.startLane + ':' + b.leftPx)}
            {#if b.label}
              <span class="band-label" style="top: {bandLabelTop(b)}px; left: {b.leftPx}px;">{b.label}</span>
            {/if}
          {/each}
        </div>
      {/if}
      {#each vLaneEvents as { e, i } (e.uid)}
        <EventPill
          event={e}
          isMatch={matchUids.has(e.uid)}
          isCurrent={currentMatchUid === e.uid}
          isPast={isPastEvent(e)}
          isFocused={isFocusedRow && focus.eventIndex === i}
          feedColor={feed.color}
          feedStyle={feed.style}
          feedCategory={feed.category}
          feedId={feed.id}
          onFocusEvent={focusByUid}
          dragSource={dragSourceFor(e)}
          resizable={e.allDay && (e.spanDays ?? 1) <= 1}
          isDragging={dragGhost?.uid === e.uid}
        />
      {/each}
      {#if dragGhost}
        <div
          class="drag-ghost"
          style="left: {dragGhost.left}px; width: {dragGhost.width}px; top: {dragGhost.top}px; height: {laneH - 1}px;"
          aria-hidden="true"
        >
          <span class="drag-readout" data-mono>{dragGhost.label}</span>
        </div>
      {/if}
    </div>
  {:else}
    <div class="row-collapsed">
      {#each vThick as o (o.left)}
        <i class="holiday-strip" style="left: {o.left}px; width: {o.width}px"></i>
      {/each}
      {#each vThin as o (o.left)}
        <i class="observance-strip" style="left: {o.left}px; width: {o.width}px"></i>
      {/each}
      {#each vDots as { d, i } (d.ev.uid)}
        <button
          type="button"
          class={d.multiDay ? 'span-bar' : 'dot'}
          data-cal-color={d.ev.ruleColor ?? feed.color ?? null}
          data-style={d.styleAttr}
          data-past={isPastEvent(d.ev) ? 'true' : null}
          data-highlight={isHighlightedDot(d.ev, i) ? 'true' : null}
          data-focused={isFocusedRow && focus.eventIndex === i ? 'true' : null}
          data-match={matchUids.has(d.ev.uid) ? 'true' : null}
          data-selected={selection.uids.has(d.ev.uid) ? 'true' : null}
          style={d.multiDay
            ? `left: ${d.leftPx}px; width: ${d.widthPx}px`
            : `left: ${d.px + pxPerDay / 2}px`}
          aria-label={dotLabel(d.ev)}
          onpointerenter={(e) => onDotEnter(e, d.ev)}
          onpointerleave={onDotLeave}
          onclick={() => {
            focus.feedId = feed.id;
            focus.eventIndex = i;
            openDot(d.ev);
          }}
        ></button>
      {/each}
    </div>
  {/if}
</section>

<style>
  .row {
    position: relative;
    width: max-content;
    min-width: 100%;
    /* No opaque fill: the page paper shows through, letting the full-height day /
       month rules (z0, behind the rows) read through the empty parts of the row
       while pills and the separators below draw on top. */
    background: transparent;
    /* Feed separators use the weekend tint's derived colour (--weekend-bg, a
       solid paper/ink mix — not the translucent overlay) so they read as a soft
       rule tied to the weekend/blocking pattern. */
    border-top: var(--border-w) solid var(--weekend-bg);
    box-sizing: border-box;
    transition: border-color var(--dur-ink) ease;
  }
  /* The first feed sits right under the time header's own rule, which reads as
     a doubled line — paint its top border in the page colour instead (the
     border stays, so row heights don't shift between first and later rows). */
  .row:first-of-type {
    border-top-color: var(--paper-color);
  }
  .row:last-of-type {
    border-bottom: var(--border-w) solid var(--weekend-bg);
  }
  /* A row just focused inks its separators (the header title's underline was
     dropped in favour of this), then fades back to grey: data-ink is rowInk
     (state.svelte.ts), lit by any row focus — tap, hover, keyboard moves, or
     DOM focus entering the row (onfocusin) — and cleared ROW_INK_FADE_MS on. */
  .row[data-ink='true'] {
    border-color: var(--ink-color);
  }
  .row-body {
    position: relative;
    box-sizing: border-box;
    /* Isolate each row's layout: the row's pills are absolutely positioned, so a
       change in one row can't reflow another. Paint is left uncontained so
       hovered pills can still reveal their full label past the row edge. */
    contain: layout;
  }
  /* Location bands (sub-lanes by location): a dashed rule between bands, under
     the pills, and each band's name riding on its top edge in small muted type
     with the pills' paper halo, so it stays legible over a pill. */
  .band-rule {
    position: absolute;
    left: 0;
    height: 0;
    border-top: var(--border-w) dashed var(--weekend-bg);
    pointer-events: none;
    z-index: 0;
  }
  .band-labels {
    position: sticky;
    left: 0;
    width: 0;
    height: 0;
    z-index: 3;
    pointer-events: none;
  }
  /* Past band labels sit at their band's first pill and scroll with it. */
  .band-labels-past {
    position: absolute;
    top: 0;
  }
  .band-label {
    position: absolute;
    left: 8px;
    transform: translateY(-50%);
    max-width: 40vw;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* Room for the halo stroke, which overflow: hidden would otherwise clip. */
    padding: 0 2px;
    font-size: var(--fs-10);
    line-height: 1.4;
    color: var(--ink-muted);
    /* The same paper halo as a pill's title and meta text (EventPill). */
    paint-order: stroke fill;
    -webkit-text-stroke: var(--stroke-w) var(--paper-color);
    text-shadow: 0 0 1px var(--paper-color);
  }
  /* Where a dragged pill will land: a dashed accent outline, with the landing
     date read out beside it in the "point in time" recipe (accent + paper halo) —
     to the right, since above it the row header would cover the first lane. */
  .drag-ghost {
    position: absolute;
    box-sizing: border-box;
    border: var(--border-w) dashed var(--accent-color);
    border-radius: var(--pill-radius);
    pointer-events: none;
    z-index: 4;
    transition: left var(--dur-quick) ease-out, width var(--dur-quick) ease-out;
  }
  .drag-readout {
    position: absolute;
    top: 50%;
    left: 100%;
    margin-left: 4px;
    transform: translateY(-50%);
    font-size: var(--fs-10);
    line-height: 1.2;
    white-space: nowrap;
    color: var(--accent-color);
    filter: var(--clock-halo);
  }
  .row-collapsed {
    position: relative;
    height: 16px;
  }
  .observance-strip {
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 0;
    background-image: repeating-linear-gradient(
      45deg,
      transparent 0,
      transparent 9px,
      var(--holiday-stripe) 9.5px,
      transparent 10px
    );
    background-attachment: fixed;
    opacity: 0.6;
  }
  .holiday-strip {
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 0;
    background-image: repeating-linear-gradient(
      45deg,
      transparent 0,
      transparent 4px,
      var(--holiday-stripe) 4.5px,
      transparent 5px
    );
    background-attachment: fixed;
    opacity: 0.6;
  }
  .dot {
    position: absolute;
    top: 50%;
    width: 10px;
    height: 10px;
    border-radius: 999px;
    border: var(--border-w) solid var(--ink-color);
    padding: 0;
    /* Same translucent fill as expanded pills (page colour, or the calendar tint
       via the --pill-fill overrides below), so a collapsed pill carries its bg
       colour too. */
    background: var(--pill-fill);
    transform: translate(-50%, -50%);
    cursor: pointer;
  }
  .dot:focus {
    outline: none;
  }
  .span-bar {
    position: absolute;
    top: 50%;
    height: 10px;
    border: var(--border-w) solid var(--ink-color);
    border-radius: 999px;
    padding: 0;
    background: var(--pill-fill);
    transform: translateY(-50%);
    cursor: pointer;
  }
  .span-bar:focus {
    outline: none;
  }
  /* Carry the calendar's colour as the outline AND flip --pill-fill to the tint,
     matching expanded pills (styles/global.css) so the fill above is coloured. */
  .dot[data-cal-color='peach'], .span-bar[data-cal-color='peach'] { border-color: var(--cal-peach-border); --pill-fill: color-mix(in srgb, var(--cal-peach-bg) var(--pill-alpha), transparent); }
  .dot[data-cal-color='amber'], .span-bar[data-cal-color='amber'] { border-color: var(--cal-amber-border); --pill-fill: color-mix(in srgb, var(--cal-amber-bg) var(--pill-alpha), transparent); }
  .dot[data-cal-color='mint'], .span-bar[data-cal-color='mint'] { border-color: var(--cal-mint-border); --pill-fill: color-mix(in srgb, var(--cal-mint-bg) var(--pill-alpha), transparent); }
  .dot[data-cal-color='teal'], .span-bar[data-cal-color='teal'] { border-color: var(--cal-teal-border); --pill-fill: color-mix(in srgb, var(--cal-teal-bg) var(--pill-alpha), transparent); }
  .dot[data-cal-color='sky'], .span-bar[data-cal-color='sky'] { border-color: var(--cal-sky-border); --pill-fill: color-mix(in srgb, var(--cal-sky-bg) var(--pill-alpha), transparent); }
  .dot[data-cal-color='lavender'], .span-bar[data-cal-color='lavender'] { border-color: var(--cal-lavender-border); --pill-fill: color-mix(in srgb, var(--cal-lavender-bg) var(--pill-alpha), transparent); }
  /* Outline keeps the calendar's tinted border but no colour fill — just the
     outline, even when a colour applies (2-attr selector beats the fills above). */
  .dot[data-style='outline'][data-cal-color], .span-bar[data-style='outline'][data-cal-color] { --pill-fill: color-mix(in srgb, var(--paper-color) var(--pill-alpha), transparent); }
  /* Carry the event/feed style variant, matching expanded pills. (striked has
     no pill representation since pills carry no text.) */
  .dot[data-style='bold'], .span-bar[data-style='bold'] { border-width: calc(var(--border-w) + 1px); }
  /* Border-box keeps the outer size fixed, so bump the round pill 2px to keep
     the heavier 2px border visually balanced. */
  .dot[data-style='bold'] { width: 12px; height: 12px; }
  /* Keep the focused bold dot at its prior size so focusing doesn't enlarge it. */
  .dot[data-style='bold'][data-focused='true'] { width: 11px; height: 11px; }
  .dot[data-style='dashed'], .span-bar[data-style='dashed'] { border-style: dashed; }
  .dot[data-style='inverted'], .span-bar[data-style='inverted'] { background: var(--ink-color); }
  /* A colored calendar's Solid (inverted) pill fills with the calendar bg tint
     and keeps its coloured border — identical to expanded pills, so a feed looks
     the same collapsed or expanded. 2-attr selectors beat the ink fill above. */
  .dot[data-style='inverted'][data-cal-color='peach'], .span-bar[data-style='inverted'][data-cal-color='peach'] { background: var(--cal-peach-bg); }
  .dot[data-style='inverted'][data-cal-color='amber'], .span-bar[data-style='inverted'][data-cal-color='amber'] { background: var(--cal-amber-bg); }
  .dot[data-style='inverted'][data-cal-color='mint'], .span-bar[data-style='inverted'][data-cal-color='mint'] { background: var(--cal-mint-bg); }
  .dot[data-style='inverted'][data-cal-color='teal'], .span-bar[data-style='inverted'][data-cal-color='teal'] { background: var(--cal-teal-bg); }
  .dot[data-style='inverted'][data-cal-color='sky'], .span-bar[data-style='inverted'][data-cal-color='sky'] { background: var(--cal-sky-bg); }
  .dot[data-style='inverted'][data-cal-color='lavender'], .span-bar[data-style='inverted'][data-cal-color='lavender'] { background: var(--cal-lavender-bg); }
  /* Past pills mute the same way expanded rows do. */
  .dot[data-past='true'], .span-bar[data-past='true'] {
    opacity: var(--past-opacity);
  }
  .dot[data-style='muted'], .span-bar[data-style='muted'] { opacity: 0.4; }
  /* Hidden drops the calendar colour for the theme's ink and paper, as the
     expanded pills do (global.css) — no grayscale filter, which turned a tinted
     theme's own colours grey. Two attributes beat the cal-color tints above. */
  .dot[data-style='hidden'], .span-bar[data-style='hidden'] {
    opacity: 0.25;
    border-color: var(--ink-color);
    --pill-fill: color-mix(in srgb, var(--paper-color) var(--pill-alpha), transparent);
    cursor: not-allowed;
  }
  .dot[data-match='true'] {
    background: var(--accent-color);
    border-color: var(--accent-color);
    outline: 2px solid var(--accent-color);
    outline-offset: 1px;
  }
  .dot[data-highlight='true'],
  .dot[data-selected='true'] {
    background: var(--accent-color);
    border-color: var(--accent-color);
  }
  .span-bar[data-match='true'],
  .span-bar[data-highlight='true'],
  .span-bar[data-selected='true'] {
    background: var(--accent-color);
    border-color: var(--accent-color);
  }
</style>
